'use client';

import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useSpring, a, useTrail, SpringValue } from '@react-spring/web';
import Logo from '@/assets/svg/HeaderLogo';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import Header from '@/components/layout/Header';
import { Locale } from '@/enums/locale.enum';
import { cn } from '@/lib/utils';
import useMeasure from 'react-use-measure';
import { useMediaQuery } from 'usehooks-ts';

interface Props {
  text: any;
  headerText: any;
  tags: any;
  navText: any;
  lang: Locale;
}

const fast = { tension: 1200, friction: 40 };
const slow = { mass: 10, tension: 200, friction: 50 };

export default function ({
  text: dict,
  navText,
  headerText,
  lang,
  tags,
}: Props) {
  const [showreel, setShowreel] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const mobile = useMediaQuery('(max-width: 767px)');

  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const handleScroll = () => {
    const currentScrollPosition = window.scrollY;

    if (
      !showreel &&
      currentScrollPosition > height / 3 &&
      currentScrollPosition > scrollPosition
    ) {
      setShowreel(true);
    }

    setScrollPosition(currentScrollPosition);
  };

  useEffect(() => {
    if (!showreel) {
      window.addEventListener('scroll', handleScroll);
    } else {
      window.removeEventListener('scroll', handleScroll);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showreel]);

  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionRef.current) return;

    const handleResize = () => {
      if (!sectionRef.current) return;
      const width = sectionRef.current.getBoundingClientRect().width;
      const height = sectionRef.current.getBoundingClientRect().height;
      api.set({
        xy: [width / 2, mobile ? height / 2.5 : height / 2],
      });
      setWidth(width);
      setHeight(height);
    };

    handleResize();

    const observer = new ResizeObserver(() => {
      handleResize();
    });

    observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [sectionRef]);

  const [isMountded, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setShowreel(false);
  }, []);

  const headerStyle = useSpring({
    y: showreel ? 0 : -150,
    opacity: showreel ? 1 : 0,
    config: { tension: 280, friction: 60 },
  });

  const [cursorStyle, api] = useSpring(() => ({
    xy: [width / 2, height / 2],
    config: slow,
    reset: isMountded,
  }));

  const logoStyle = useSpring({
    from: { y: mobile ? 0 : 200, opacity: 0 },
    to: async (next) => {
      if (!showreel) {
        await next({ opacity: 1, config: { duration: 1500 } });
        await next({ y: 0 });
      } else {
        await next({ y: mobile ? 0 : height / 4, config: { duration: 1500 } });
        await next({ opacity: 0 });
      }
    },
    config: { tension: 280, friction: 60 },
  });

  const textStyle = useSpring({
    y: showreel ? 0 : mobile ? 0 : 800,
    color: showreel
      ? mobile
        ? '#E5E5E5'
        : '#E5E5E5'
      : mobile
      ? '#101011'
      : '#E5E5E5',
    opacity: showreel ? 1 : mobile ? 1 : 0,
    config: { tension: 280, friction: 60, duration: 1000 },
  });

  const handleMouseMove = (e: any) => {
    if (!sectionRef.current || showreel) return;

    const rect = sectionRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    api.start({ xy: [x, y] });
  };

  return (
    <div
      ref={sectionRef}
      className='relative w-full h-[960px] lg:h-[622px] xl:h-[800px] sm:h-[700px] overflow-hidden'
    >
      <div
        className='absolute inset-0 overflow-visible w-full h-full'
        onMouseMove={handleMouseMove}
      >
        <a.div style={headerStyle} className='z-[1000] absolute w-full'>
          <Header
            headerText={headerText}
            navText={navText}
            lang={lang}
            tags={tags}
            src='landing'
            sectionRef={sectionRef}
          />
        </a.div>
        {isMountded && (
          <a.div
            style={logoStyle}
            className='w-full sm:px-2 px-10 absolute  py-10'
          >
            <Logo />
          </a.div>
        )}
        <div onClick={() => setShowreel(!showreel)}>
          <Video
            width={width}
            height={height}
            showreel={showreel}
            cursorStyle={cursorStyle}
          />
        </div>
        <a.div
          style={textStyle}
          className='absolute bottom-24 sm:bottom-14 sm:px-4 sm:pt-0 right-0 flex flex-col items-end w-full  pt-8 pr-8'
        >
          <div className='2xl:max-w-[1302px] max-w-[868px] w-full text-left'>
            <h1 className='2xl:text-[55px] text-[36px] text-[#E5E5E5] lg:text-3xl md:text-2xl tracking-[-0.05em] sm:text-[25px] leading-[1.1] font-normal'>
              {dict.catchphrase}
            </h1>
          </div>
        </a.div>
      </div>
    </div>
  );
}

interface VideoProps {
  width: number;
  height: number;
  showreel: boolean;
  cursorStyle: { xy: SpringValue<number[]> };
}

const Video = ({ width, height, showreel, cursorStyle }: VideoProps) => {
  const mobile = useMediaQuery('(max-width: 767px)');
  const videoStyle = useSpring({
    width: showreel ? width : mobile ? width - 48 : 480,
    height: showreel ? height : mobile ? 228 : 318,
    opacity: !width || !height ? 0 : 1,
    transform: showreel
      ? `translate3d(${0}px,${0}px,0) translate3d(0%,0%,0)`
      : cursorStyle.xy.to((x: number, y: number) => {
          return `translate3d(${x}px,${y}px,0) translate3d(-50%,-50%,0)`;
        }),
    delay: 200,
    borderRadius: showreel ? 0 : 20,
    config: { tension: 280, friction: 60 },
  });

  return (
    <div className='h-full w-full'>
      <a.div
        className='absolute top-0 left-0 w-full h-full cursor-pointer'
        style={{
          ...videoStyle,
        }}
      >
        <a.video
          className='object-cover w-full h-full'
          autoPlay
          loop
          muted
          playsInline
          style={{
            pointerEvents: 'none',
            borderRadius: videoStyle.borderRadius.to((r) => `${r}px`),
            position: 'absolute',
          }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <source src={'/video/home-header.mp4'} type='video/mp4' />
        </a.video>
      </a.div>
    </div>
  );
};
